import React, { useState, useEffect } from 'react';

interface URLPreviewProps {
  url: string;
}

interface PreviewData {
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
  favicon?: string;
}

const URLPreview: React.FC<URLPreviewProps> = ({ url }) => {
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchPreviewData = async () => {
      try {
        setLoading(true);
        setError(false);
        
        // CORS 문제로 인해 직접 fetch는 불가능하므로 
        // 무료 API 서비스를 사용하거나 간단한 방식으로 처리
        // 여기서는 URL에서 도메인 정보만 추출하여 간단한 미리보기 생성
        const urlObj = new URL(url);
        const domain = urlObj.hostname;
        
        // 간단한 미리보기 데이터 생성
        const simplePreview: PreviewData = {
          title: `${domain}의 웹페이지`,
          description: `${url}로 이동합니다`,
          siteName: domain,
          favicon: `https://www.google.com/s2/favicons?domain=${domain}&sz=32`
        };
        
        setPreviewData(simplePreview);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (url) {
      fetchPreviewData();
    }
  }, [url]);

  if (loading) {
    return (
      <div style={{
        border: '1px solid #e9ecef',
        borderRadius: '12px',
        padding: '1rem',
        margin: '1rem 0',
        background: '#f8f9fa'
      }}>
        <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
          링크 미리보기 로딩 중...
        </div>
      </div>
    );
  }

  if (error || !previewData) {
    return (
      <div style={{
        border: '1px solid #e9ecef',
        borderRadius: '12px',
        padding: '1rem',
        margin: '1rem 0',
        background: '#f8f9fa',
        cursor: 'pointer'
      }}
      onClick={() => window.open(url, '_blank')}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: '#007bff',
          textDecoration: 'none'
        }}>
          <span>🔗</span>
          <span style={{ fontSize: '0.9rem', wordBreak: 'break-all' }}>{url}</span>
        </div>
      </div>
    );
  }

  return (
    <div 
      style={{
        border: '1px solid #e9ecef',
        borderRadius: '12px',
        overflow: 'hidden',
        margin: '1rem 0',
        cursor: 'pointer',
        transition: 'all 0.2s',
        background: 'white'
      }}
      onClick={() => window.open(url, '_blank')}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ padding: '1rem' }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem'
        }}>
          {previewData.favicon && (
            <img 
              src={previewData.favicon} 
              alt="favicon"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '4px',
                flexShrink: 0
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#2c3e50',
              marginBottom: '0.25rem',
              lineHeight: '1.3'
            }}>
              {previewData.title || '웹페이지'}
            </div>
            
            {previewData.description && (
              <div style={{
                fontSize: '0.9rem',
                color: '#6c757d',
                marginBottom: '0.5rem',
                lineHeight: '1.4'
              }}>
                {previewData.description}
              </div>
            )}
            
            <div style={{
              fontSize: '0.8rem',
              color: '#007bff',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              <span>🔗</span>
              <span style={{ wordBreak: 'break-all' }}>
                {previewData.siteName || new URL(url).hostname}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default URLPreview; 